import React from "react";

const CHART_HEIGHT = 210;
const CHART_WIDTH = 920;
const PADDING = { top: 24, right: 16, bottom: 40, left: 52 };

function formatNumber(value, isPercent) {
  if (value === null || value === undefined) return "-";
  if (isPercent) return `${value.toFixed(1)}%`;
  return Number(value).toLocaleString("en-IN", { maximumFractionDigits: 0 });
}

function buildPoints(values, minValue, maxValue) {
  const innerWidth = CHART_WIDTH - PADDING.left - PADDING.right;
  const innerHeight = CHART_HEIGHT - PADDING.top - PADDING.bottom;
  const safeRange = maxValue - minValue || 1;
  const denominator = Math.max(values.length - 1, 1);

  return values.map((value, index) => {
    const x = PADDING.left + (index / denominator) * innerWidth;
    const y = PADDING.top + ((maxValue - value) / safeRange) * innerHeight;
    return { x, y, value };
  });
}

function toPath(points) {
  if (!points.length) return "";
  return points
    .map((point, index) => `${index === 0 ? "M" : "L"} ${point.x} ${point.y}`)
    .join(" ");
}

export default function ForecastTrendChart({
  title,
  subtitle,
  labels = [],
  series = [],
  isPercent = false,
}) {
  const activeSeries = series.filter(
    (item) => Array.isArray(item.values) && item.values.length === labels.length
  );

  if (!labels.length || !activeSeries.length) return null;

  const allValues = activeSeries.flatMap((item) => item.values);
  const minRaw = Math.min(...allValues);
  const maxRaw = Math.max(...allValues);
  const padding = Math.max((maxRaw - minRaw) * 0.08, 1);
  const minValue = Math.max(0, minRaw - padding);
  const maxValue = maxRaw + padding;

  const yTicks = Array.from({ length: 5 }).map((_, index) => {
    const ratio = index / 4;
    return maxValue - (maxValue - minValue) * ratio;
  });

  return (
    <div className="graph-panel">
      <div className="graph-panel-header">
        <h4>{title}</h4>
        {subtitle ? <p>{subtitle}</p> : null}
      </div>

      <div className="graph-scroll-wrap">
        <svg
          className="trend-chart-svg"
          viewBox={`0 0 ${CHART_WIDTH} ${CHART_HEIGHT}`}
          preserveAspectRatio="xMinYMin meet"
        >
          {yTicks.map((tick, index) => {
            const y =
              PADDING.top +
              (index / (yTicks.length - 1)) * (CHART_HEIGHT - PADDING.top - PADDING.bottom);
            return (
              <g key={`tick-${index}`}>
                <line
                  x1={PADDING.left}
                  y1={y}
                  x2={CHART_WIDTH - PADDING.right}
                  y2={y}
                  stroke="#E4E4E7"
                  strokeDasharray="4 4"
                />
                <text x={12} y={y + 4} fontSize="10" fill="#71717A">
                  {formatNumber(tick, isPercent)}
                </text>
              </g>
            );
          })}

          {labels.map((label, index) => {
            const innerWidth = CHART_WIDTH - PADDING.left - PADDING.right;
            const denominator = Math.max(labels.length - 1, 1);
            const x = PADDING.left + (index / denominator) * innerWidth;
            return (
              <text key={`${label}-${index}`} x={x} y={CHART_HEIGHT - 12} fontSize="10" fill="#71717A" textAnchor="middle">
                {label}
              </text>
            );
          })}

          {activeSeries.map((item) => {
            const points = buildPoints(item.values, minValue, maxValue);
            return (
              <g key={item.key}>
                <path d={toPath(points)} fill="none" stroke={item.color} strokeWidth="2.2" />
                {points.map((point, index) => (
                  <circle key={`${item.key}-${index}`} cx={point.x} cy={point.y} r="3.2" fill={item.color}>
                    <title>{`${item.label}: ${formatNumber(point.value, isPercent)}`}</title>
                  </circle>
                ))}
              </g>
            );
          })}
        </svg>
      </div>

      <div className="graph-legend">
        {activeSeries.map((item) => (
          <div key={item.key} className="graph-legend-item">
            <span className="graph-legend-dot" style={{ backgroundColor: item.color }} />
            <span>{item.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
